import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { RoleManagement } from '@configs/role.config';
import { TranslateService } from '@ngx-translate/core';
import { AppService } from '@services/app.service';
import { CollectionService } from '@services/collection.service';
import { ConfigService } from '@services/config.service';
import { RoleManagementService } from '@services/role.management.service';
import { UserService } from '@services/user.service';
import { WebErrorService } from '@services/webError.service';
import { Subscription } from 'rxjs';
import { IdleService } from 'src/app/shared/idle.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  loginForm: FormGroup;
  fullyear = new Date().getFullYear();
  isSubmitted = false;
  loginSpinner = false;
  submitted = false;
  loginErrorMessage: string;
  userServiceSubscription$: Subscription;
  private formSubmitAttempt: boolean;
  collectionData$: Subscription | undefined;
  captchaData$: Subscription;
  validateCaptcha$: Subscription;
  checkUser$: Subscription;
  CaptchaImgUrl: string = '';
  CaptchaTextValue: string = "";
  CaptchaTokenValue: string = "";
  private _returnUrl: any;
  isValid: boolean = false;
  Interval: any;
  disableForgotPass: boolean = false;
  smallDescription: any;
  langCode: any;
  lockoutMsg: boolean = false;
  inValidEmail: boolean = false;
  constructor(
    private _activatedRoute: ActivatedRoute,
    private roleSer: RoleManagementService,
    private webErrorService: WebErrorService,
    private fb: FormBuilder,
    private userService: UserService,
    private collectionService: CollectionService,
    private router: Router,
    private appService: AppService,
    public configService: ConfigService,
    public idleService: IdleService,
    private dialog: MatDialog,
    public translate: TranslateService,
  ) { }

  ngOnInit(): void {
    this.CaptchaImgUrl = '../../../assets/favicon/images.jfif';
    this.appService.changeUserHeader(0);
    this.loginForm = this.fb.group({
      email: ['', [Validators.required,
        Validators.minLength(7),
        Validators.maxLength(124),
        Validators.pattern('[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,4}')]],
      password: ['', [Validators.required,
        Validators.maxLength(20),
        Validators.pattern('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%&*^])[a-zA-Z0-9!@#$%&*^]{12,}$'),
        this.validatePassword.bind(this),
      ]],
      captchaText: ['', Validators.required],
      rememberMe: [false]
    });
    sessionStorage.clear();
    const returnUrl = this._activatedRoute.snapshot.queryParams['returnUrl'];
    this._returnUrl = returnUrl ? returnUrl : '';

    if (localStorage.getItem('language')) {
        let lng=localStorage.getItem('language')
        this.translate.use(lng);
        this.langCode = lng;
    }


    this.checkIfIdleLogout();
    this.GetCaptcha();

    this.Interval= setInterval(() => {
      this.GetCaptcha();
    }, 300000);
  }

  contentDisplay(languageCode) {
    if(languageCode === 'es-ES'){
      this.smallDescription = this.configService.themeConfig.DescSpanish;
    } else if(languageCode === 'pt-PT'){
      this.smallDescription = this.configService.themeConfig.DescPortugese;
    } else {
      this.smallDescription = this.configService.themeConfig.Description
    }
  }

  //get captcha
  GetCaptcha() {
    if(this.captchaData$){this.captchaData$.unsubscribe();}
    this.captchaData$= this.userService.getCaptcha()
    .subscribe(data => {
    if(data) {
      this.CaptchaImgUrl = data.dntCaptchaImgUrl;
      this.CaptchaTokenValue = data.dntCaptchaTokenValue;
      this.CaptchaTextValue = data.dntCaptchaTextValue;
    }
    })
  }

  resendCaptcha() {
    this.GetCaptcha();
  }

  get f() { return this.loginForm.controls; }

  validatePassword(control: AbstractControl): { [key: string]: boolean } | null {
    const password = control.value;
    let email = this.loginForm?.get('email').value;
    if(password === email){
      return { passwordSameAsUsernameOrEmail: true };
    }
    return null;
  }

  isFieldInvalid(field: string) {
    return (
      (!this.loginForm.get(field).valid && this.loginForm.get(field).touched) ||
      (this.loginForm.get(field).untouched && this.formSubmitAttempt)
    );
  }

  onClickOutside(event: any, name) {
    if(event && name == 'email') {
      this.inValidEmail = true;
    } else {
      this.inValidEmail = false;
    }
  }

  login() {
    this.lockoutMsg = false;
    this.loginErrorMessage = "";
    this.submitted = true;
    if (this.loginForm.valid) {
      this.isValid = true;
      if(this.validateCaptcha$){this.validateCaptcha$.unsubscribe();}
      this.validateCaptcha$= this.userService.validateCaptcha(this.CaptchaTokenValue, this.CaptchaTextValue, this.loginForm.value.captchaText)
      .subscribe((res)=>{
        if(res && res.status === 'true') {
          if( this.checkUser$){ this.checkUser$.unsubscribe();}
          this.checkUser$= this.userService.checkUser(this.loginForm.value.email, this.loginForm.value.password)
          .subscribe((res)=>{
            let d = {email: res.data.emailId, useruuid: res.data.useruuid, password: this.loginForm.value.password, isTempPassword: res.data.isTempPassword}
            localStorage.setItem('userData', JSON.stringify(d));
            localStorage.setItem('rememberMe', this.loginForm.value.rememberMe)
            if (res.data.isTempPassword == false && res.data.isTwoFactorAuthentication == false) {
              this.loginFun();
            } else if (res.data.isTempPassword == true && res.data.isTwoFactorAuthentication == false) {
              this.router.navigate(['/changePassword']);
            }
            else {
              this.router.navigate(["./twoFactorAuth"]);
            }
          },(error)=>{
            this.resendCaptcha();
            this.loginErrorMessage = error.error.message;
            if (this.loginErrorMessage.match(this.translate.instant('userLogin.loginForm.errorMsg'))) {
              this.disableForgotPass = true;
              this.lockoutMsg = true;
            }
            this.isValid = false;
          })

          // this.userServiceSubscription$ = this.userService.login(this.loginForm.value.email, this.loginForm.value.password).subscribe((data) => {
          //   localStorage.clear();
          //   const langId = data.data.langId;
          //   langId ? localStorage.setItem('langId',langId) : localStorage.setItem('langId','1');
          //   const langCode = data.data.languagecode;
          //   langCode !== undefined ? localStorage.setItem('language',langCode) : null;

          //   this.roleSer.startRoleManagement();
          //   this.collectionData$ = this.collectionService.getCollection()?.subscribe((collectionData: any) => {
          //     let collections = collectionData?.data;
          //     sessionStorage.removeItem('aiToken');
          //     if (collectionData.data && collectionData.data.length > 0) {
          //       sessionStorage.defaultcollectionid = JSON.stringify(collections[0].collectionid);
          //       sessionStorage.defaultcollectionuuid = JSON.stringify(collections[0].collectionuuId);
          //       this.roleSer.getAccessDetails(RoleManagement.Items, true);
          //     }

          //     this.resetIdealLogoutFlag();
          //   },
          //     (error: any) => {
          //       this.webErrorService.handleAPIErrors(error);
          //     });
          // },
          //   error => { this.loginErrorMessage = error.error.message; });
        }
        else{
          this.resendCaptcha();
          this.isValid = false;
          this.loginErrorMessage = res.message

        }
      },(error: any)=>{
        this.loginErrorMessage = error.error.message;
      })

    }
  }

  loginFun() {
    let loginPayload = {
      "emailId": this.loginForm.value.email,
      "password": this.loginForm.value.password
    }
    this.userService.signIn(loginPayload).subscribe((data) => {
      if (data.status != 'false') {
        localStorage.clear();
        const langId = data.data.langId;
        langId ? localStorage.setItem('langId',langId) : localStorage.setItem('langId','1');
        const langCode = data.data.languagecode;
        langCode !== undefined ? localStorage.setItem('language',langCode) : null;
        localStorage.setItem('currentUser', JSON.stringify(data.data));
        localStorage.setItem('rememberMe', this.loginForm.value.rememberMe)
        this.roleSer.startRoleManagement();

        this.collectionData$ = this.collectionService.getCollection()?.subscribe((collectionData: any) => {
          let collections = collectionData?.data;
          sessionStorage.removeItem('aiToken');
          if (collectionData.data && collectionData.data.length > 0) {
            sessionStorage.defaultcollectionid = JSON.stringify(collections[0].collectionid);
            sessionStorage.defaultcollectionuuid = JSON.stringify(collections[0].collectionuuId);
            localStorage.defaultcollectionid = JSON.stringify(collections[0].collectionid);
            localStorage.defaultcollectionuuid = JSON.stringify(collections[0].collectionuuId);
            this.roleSer.getAccessDetails(RoleManagement.Items, true);
          }

          this.resetIdealLogoutFlag();
        },
          (error: any) => {
            this.isValid = false;
            this.webErrorService.handleAPIErrors(error);
          });
      } else {
        this.isValid = false;
        this.loginErrorMessage = data.message;
      }
    },
    error => { this.loginErrorMessage = error.error.message; });
  }

  resetPwd() {
    this.router.navigate(["./reset-password"])
  }

  onClickPrivacyPolicy() {
    window.open('assets/Privacy_policy.html', '_blank');
  }

  checkIfIdleLogout(): void {
    if (localStorage.Idle_logout && !this.userService.isAuthenticated() && !this.idleService.showIdealLogoutMessage) {
      this.idleService.showIdealLogoutMessage = true;

      console.log("idle")
      this.dialog.closeAll();
    }
  }

  resetIdealLogoutFlag(): void {
    this.idleService.showIdealLogoutMessage = false;
    localStorage.removeItem('Idle_logout');
  }

   ngOnDestroy(): void {
    if(this.captchaData$){this.captchaData$.unsubscribe();}
    if( this.checkUser$){ this.checkUser$.unsubscribe();}
    if(this.validateCaptcha$){this.validateCaptcha$.unsubscribe();}
    if(this.Interval){clearInterval(this.Interval)}
    localStorage.removeItem('loginDisabled');
    localStorage.removeItem('disabledTimestamp');
    localStorage.removeItem('UnsuccessMessage');
  }
}
